import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Clock, 
  Send,
  MessageSquare,
  Percent,
  Target,
  TrendingUp,
  RefreshCw,
  Zap,
  Globe,
  Loader2,
  BarChart3,
  AlertCircle,
  Mail,
  Eye,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BacklinkAutomationPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [sentEmails, setSentEmails] = useState([]);
  const [showEmails, setShowEmails] = useState(false);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const { getAuthHeaders } = useAuth();
  const { currentSite } = useSite();

  const fetchSentEmails = async () => {
    if (!currentSite?.id) return;
    setLoadingEmails(true);
    try {
      const response = await axios.get(
        `${API}/backlinks/outreach?site_id=${currentSite.id}`,
        { headers: getAuthHeaders() }
      );
      // Filter only sent emails
      const sent = response.data.filter(e => e.status === 'sent').slice(0, 20);
      setSentEmails(sent);
      setShowEmails(true);
    } catch (error) {
      toast.error('Eroare la încărcarea emailurilor');
    } finally {
      setLoadingEmails(false);
    }
  };

  const fetchStats = async () => {
    if (!currentSite?.id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/backlinks/outreach/detailed-stats?site_id=${currentSite.id}`, 
        { headers: getAuthHeaders() }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Fallback to basic status endpoint
      try {
        const fallback = await axios.get(`${API}/backlinks/automation-status`, { headers: getAuthHeaders() });
        const siteData = fallback.data.sites?.find(s => s.site_id === currentSite.id);
        setStats({
          overview: {
            total_sent: siteData?.emails_sent_7_days || 0,
            total_responded: siteData?.responses_received || 0,
            total_drafts: 0,
            total_pending: 0,
            response_rate: 0,
            conversions: 0,
            conversion_rate: 0
          },
          daily_stats: [],
          top_domains: [],
          weekly_comparison: { this_week: siteData?.emails_sent_today || 0, last_week: siteData?.emails_sent_yesterday || 0, change_percent: 0 },
          emails_today: siteData?.emails_sent_today || 0,
          max_emails_today: 15
        });
      } catch (e) {
        toast.error('Eroare la încărcarea statisticilor');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerAutomation = async () => {
    setTriggering(true);
    try {
      const response = await axios.post(`${API}/backlinks/trigger-automation`, {}, { headers: getAuthHeaders() });
      if (response.data.success) {
        toast.success('🚀 ' + response.data.message, { duration: 6000 });
        // Refresh stats after delay
        setTimeout(() => fetchStats(), 30000);
      } else {
        toast.error(response.data.error || 'Eroare');
      }
    } catch (error) {
      toast.error('Eroare: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTriggering(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [currentSite?.id]);

  useEffect(() => {
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentSite?.id]);

  if (!currentSite) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <AlertCircle className="w-12 h-12 mb-4 opacity-50" />
        <p className="text-lg">Selectează un site pentru a vedea statisticile</p>
      </div>
    );
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const overview = stats?.overview || {};
  const weeklyComparison = stats?.weekly_comparison || {};
  const topDomains = stats?.top_domains || [];
  const emailsToday = stats?.emails_today || 0;
  const maxEmailsToday = 15; // Max per site per day

  return (
    <div className="space-y-6" data-testid="backlink-automation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Automatizare Backlinks</h1>
          <p className="text-muted-foreground">
            Dashboard statistici pentru <span className="text-primary font-medium">{currentSite.site_name || currentSite.site_url}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleTriggerAutomation} 
            disabled={triggering}
            className="bg-green-600 hover:bg-green-700"
            data-testid="trigger-automation-btn"
          >
            {triggering ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Declanșează Acum
          </Button>
          <Button variant="outline" onClick={fetchStats} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Reîmprospătează
          </Button>
        </div>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Total Trimise */}
        <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Trimise</p>
                <p className="text-3xl font-bold">{overview.total_sent || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-emerald-500/20">
                <Send className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Răspunsuri */}
        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Răspunsuri</p>
                <p className="text-3xl font-bold">{overview.total_responded || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-blue-500/20">
                <MessageSquare className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rată Răspuns */}
        <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rată Răspuns</p>
                <p className="text-3xl font-bold">{overview.response_rate || 0}%</p>
              </div>
              <div className="p-3 rounded-full bg-amber-500/20">
                <Percent className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conversii */}
        <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversii</p>
                <p className="text-3xl font-bold">{overview.conversions || 0}</p>
              </div>
              <div className="p-3 rounded-full bg-purple-500/20">
                <Target className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rată Conversie */}
        <Card className="bg-gradient-to-br from-pink-500/20 to-pink-600/10 border-pink-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rată Conversie</p>
                <p className="text-3xl font-bold">{overview.conversion_rate || 0}%</p>
              </div>
              <div className="p-3 rounded-full bg-pink-500/20">
                <TrendingUp className="w-6 h-6 text-pink-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row - 3 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Comparație Săptămânală */}
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-2">Comparație Săptămânală</p>
            <div className="flex items-end gap-3">
              <p className="text-3xl font-bold">{weeklyComparison.this_week || 0}</p>
              <div className={`flex items-center text-sm ${weeklyComparison.change_percent >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                <TrendingUp className="w-4 h-4 mr-1" />
                {weeklyComparison.change_percent || 0}%
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              emailuri săptămâna asta vs săpt. trecută ({weeklyComparison.last_week || 0})
            </p>
          </CardContent>
        </Card>

        {/* Următoarea Rulare */}
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-2">Următoarea Rulare Automată</p>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-500/20">
                <Clock className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-3xl font-bold">12:30</p>
                <p className="text-xs text-muted-foreground">Ora României (zilnic)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Emailuri Azi */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Emailuri Azi</p>
              <p className="text-sm font-medium">{emailsToday} / {maxEmailsToday}</p>
            </div>
            <p className="text-sm mb-2">Trimise</p>
            <Progress value={(emailsToday / maxEmailsToday) * 100} className="h-2" />
          </CardContent>
        </Card>
      </div>

      {/* Third Row - Status & Top Domains */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Status Emailuri */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5" />
              Status Emailuri
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span>Draft</span>
                </div>
                <span className="font-medium">{overview.total_drafts || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>În așteptare</span>
                </div>
                <span className="font-medium">{overview.total_pending || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span>Trimise</span>
                </div>
                <span className="font-medium">{overview.total_sent || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span>Cu răspuns</span>
                </div>
                <span className="font-medium">{overview.total_responded || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Domenii Responsive */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="w-5 h-5" />
              Top Domenii Responsive
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topDomains.length > 0 ? (
              <div className="space-y-3">
                {topDomains.map((domain, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm">{domain.domain}</span>
                    <Badge variant="secondary">{domain.responses} răspunsuri</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <MessageSquare className="w-10 h-10 mb-2 opacity-50" />
                <p>Niciun răspuns încă</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Buton Vezi Emailuri Trimise */}
      <div className="mt-6">
        <Button 
          onClick={fetchSentEmails} 
          variant="outline"
          disabled={loadingEmails}
          className="w-full"
        >
          {loadingEmails ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-2" />
          )}
          Vezi Emailuri Trimise ({overview.total_sent || 0})
        </Button>
      </div>

      {/* Modal/Secțiune Emailuri Trimise */}
      {showEmails && (
        <Card className="mt-4">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="w-5 h-5" />
              Emailuri Trimise Recent ({sentEmails.length})
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowEmails(false)}>
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            {sentEmails.length > 0 ? (
              <div className="space-y-4">
                {sentEmails.map((email, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-muted/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{email.contact_email}</span>
                      <Badge variant="outline" className="text-xs">
                        {new Date(email.sent_at).toLocaleDateString('ro-RO')}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">
                      Către: {email.backlink_domain}
                    </div>
                    <div className="text-sm font-medium mb-1">
                      {email.email_subject}
                    </div>
                    <div className="text-sm text-muted-foreground whitespace-pre-wrap border-t pt-2 mt-2">
                      {email.email_body || email.email_content || 'Conținut indisponibil'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Mail className="w-10 h-10 mb-2 opacity-50" />
                <p>Niciun email trimis încă</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
